import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, ExternalLink, CheckCircle, AlertCircle, X, Mail, Calendar, Video, Smartphone, RefreshCw, Clock } from 'lucide-react';
import api, { API_BASE_URL } from '../utils/api';
import IntegrationSetupWizard from './IntegrationSetupWizard';

const PersonalIntegrations = ({ user }) => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [integrationStatuses, setIntegrationStatuses] = useState({});
  const [googleHealth, setGoogleHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Fetch Google token health
  const fetchGoogleHealth = async () => {
    try {
      setHealthLoading(true);
      const response = await api.get('/integrations/google/health');
      setGoogleHealth(response.data);
    } catch (error) {
      console.error('Failed to fetch Google health:', error);
      setGoogleHealth(null);
    } finally {
      setHealthLoading(false);
    }
  };

  // Available personal integrations
  const availablePersonalIntegrations = [
    {
      id: 'google-workspace',
      name: 'Google Calendar',
      description: 'Import meetings from Google Calendar using read-only access',
      icon: '📧',
      category: 'Productivity',
      status: integrationStatuses['google']?.connected ? 'connected' : 'available',
      connectedEmail: integrationStatuses['google']?.email,
      hasSetupWizard: true,
      isImplemented: true
    }
  ];

  const handleConnect = (integrationId) => {
    const integration = availablePersonalIntegrations.find(i => i.id === integrationId);
    if (integration?.hasSetupWizard) {
      setSelectedIntegration(integration);
      setShowWizard(true);
    }
  };

  const handleDisconnect = async (integrationId) => {
    const confirmed = window.confirm(
      'Are you sure you want to disconnect this integration? This will remove access to your connected account data.'
    );
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      
      // Map integration IDs to provider types
      const providerMap = {
        'google-workspace': 'google'
      };
      
      const provider = providerMap[integrationId];
      
      const response = await api.delete(`/integrations/${provider}`);
      
      if (response.data.success) {
        showNotification('success', 'Integration disconnected successfully');
        await fetchIntegrationStatuses(); // Refresh status
      } else {
        showNotification('error', 'Failed to disconnect integration');
      }
    } catch (error) {
      console.error('Failed to disconnect integration:', error);
      showNotification('error', 'Failed to disconnect integration');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToken = async (integrationId) => {
    try {
      setHealthLoading(true);
      
      // Map integration IDs to provider types
      const providerMap = {
        'google-workspace': 'google'
      };
      
      const provider = providerMap[integrationId];
      
      const response = await api.post(`/integrations/${provider}/refresh-token`, {});
      
      if (response.data.success) {
        showNotification('success', `Access token refreshed successfully - valid for ${response.data.days_remaining || 7} days`);
        // Refresh health status after successful token refresh
        if (provider === 'google') {
          await fetchGoogleHealth();
        }
      } else {
        showNotification('error', 'Failed to refresh token - may need to reconnect');
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      showNotification('error', 'Failed to refresh token - may need to reconnect');
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonalIntegrations();
    fetchIntegrationStatuses();
    fetchGoogleHealth();

    // Listen for integration updates (e.g., after OAuth success)
    const handleIntegrationUpdate = (event) => {
      console.log('Integration updated, refreshing status:', event.detail);
      fetchIntegrationStatuses();
      fetchPersonalIntegrations();
      fetchGoogleHealth();
    };

    window.addEventListener('integrationUpdated', handleIntegrationUpdate);

    // Cleanup event listener
    return () => {
      window.removeEventListener('integrationUpdated', handleIntegrationUpdate);
    };
  }, []);

  const fetchPersonalIntegrations = async () => {
    try {
      const response = await api.get(`/users/${user.id}/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Failed to fetch personal integrations:', error);
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrationStatuses = async () => {
    try {
      const response = await api.get('/integrations/status');
      console.log('Integration statuses fetched:', response.data);
      setIntegrationStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch integration statuses:', error);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (!window.confirm('Are you sure you want to remove this integration?')) return;

    try {
      await api.delete(`/users/${user.id}/integrations/${integrationId}`);
      
      setIntegrations(prev => prev.filter(int => int.id !== integrationId));
      showNotification('success', 'Integration removed successfully');
    } catch (error) {
      console.error('Failed to delete integration:', error);
      showNotification('error', 'Failed to remove integration');
    }
  };

  const personalIntegrationTypes = [
    {
      type: 'google_calendar',
      name: 'Google Calendar',
      description: 'Sync your calendar events and meetings with client timelines',
      icon: '📅',
      features: ['Meeting Sync', 'Client Appointment Tracking', 'Time Blocking'],
      setupType: 'oauth',
      privacy: 'Your calendar events are synced only when they include client contacts'
    }
  ];

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-800'
            : 'bg-red-100 border border-red-200 text-red-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification({ show: false, type: '', message: '' })}
            className="text-current opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Personal Integrations</h3>
            <p className="text-sm text-gray-600 mt-1">
              Connect Google Calendar to import meetings into Athena
            </p>
          </div>
          <button
            onClick={() => {
              fetchIntegrationStatuses();
              fetchPersonalIntegrations();
              showNotification('success', 'Integration status refreshed');
            }}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            title="Refresh integration status"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
        {/* Personal vs Company Integrations Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Personal Integrations</strong> are limited to Google Calendar read-only meeting import so Athena stays focused on project management.
          </p>
        </div>

        {/* Privacy Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-600 text-sm">🔒</span>
            </div>
            <div className="text-sm">
              <h4 className="font-medium text-blue-900 mb-1">Your Privacy is Protected</h4>
              <p className="text-blue-700">
                Google Calendar sync is read-only and only imports meeting information needed for Athena scheduling and project context.
              </p>
            </div>
          </div>
        </div>

        {/* Available Personal Integrations */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Available Integrations</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availablePersonalIntegrations.map((integration) => {
              // Get health info for Google integration
              const isGoogle = integration.id === 'google-workspace';
              const health = isGoogle ? googleHealth : null;
              
              return (
              <div
                key={integration.id}
                className={`p-6 border rounded-lg transition-all bg-white hover:shadow-sm ${
                  integration.status === 'connected' 
                    ? health?.health === 'expired' || health?.health === 'needs_refresh'
                      ? 'border-red-300 bg-red-50'
                      : health?.health === 'warning'
                        ? 'border-yellow-300 bg-yellow-50'
                        : 'border-green-300 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                    <span className="text-white text-xl">{integration.icon}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {integration.status === 'connected' ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-600">Connected</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 text-blue-500" />
                        <span className="text-xs text-blue-600">Available</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-900 mb-2">{integration.name}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{integration.description}</p>
                  {integration.connectedEmail && (
                    <p className="text-xs text-green-600 mt-1">Connected: {integration.connectedEmail}</p>
                  )}
                  
                  {/* Token Health Status for Google */}
                  {isGoogle && integration.status === 'connected' && health && (
                    <div className={`mt-3 p-2 rounded-md ${
                      health.health === 'healthy' ? 'bg-green-100' :
                      health.health === 'warning' ? 'bg-yellow-100' :
                      'bg-red-100'
                    }`}>
                      <div className="flex items-center space-x-2">
                        {health.health === 'healthy' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : health.health === 'warning' ? (
                          <Clock className="w-4 h-4 text-yellow-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-600" />
                        )}
                        <span className={`text-xs font-medium ${
                          health.health === 'healthy' ? 'text-green-700' :
                          health.health === 'warning' ? 'text-yellow-700' :
                          'text-red-700'
                        }`}>
                          {health.days_remaining > 0 
                            ? `${health.days_remaining} days remaining`
                            : health.health === 'needs_refresh' 
                              ? 'Token invalid - refresh required'
                              : 'Token expired - refresh required'
                          }
                        </span>
                      </div>
                      {health.token_works === false && (
                        <p className="text-xs text-red-600 mt-1">⚠️ Token verification failed</p>
                      )}
                    </div>
                  )}
                  {isGoogle && integration.status === 'connected' && healthLoading && (
                    <div className="mt-3 p-2 rounded-md bg-gray-100">
                      <div className="flex items-center space-x-2">
                        <RefreshCw className="w-4 h-4 text-gray-500 animate-spin" />
                        <span className="text-xs text-gray-600">Checking token health...</span>
                      </div>
                    </div>
                  )}
                </div>

                {integration.status === 'connected' ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleDisconnect(integration.id)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Disconnect
                    </button>
                    <button
                      onClick={() => handleRefreshToken(integration.id)}
                      disabled={healthLoading}
                      className={`px-3 py-2 border text-sm font-medium rounded-md transition-colors ${
                        health?.health === 'expired' || health?.health === 'needs_refresh' || health?.health === 'warning'
                          ? 'border-orange-400 text-orange-700 bg-orange-100 hover:bg-orange-200'
                          : 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100'
                      }`}
                      title={health?.health === 'healthy' ? 'Refresh access token' : 'Refresh required - click to refresh'}
                    >
                      <RefreshCw className={`w-4 h-4 ${healthLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleConnect(integration.id)}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Connect {integration.name}
                  </button>
                )}
              </div>
            );
            })}
          </div>
        </div>

        {/* Connected Integrations - Filter to only show personal integrations (exclude company integrations like gohighlevel) */}
        {integrations.filter(i => ['google_calendar', 'google', 'google_workspace'].includes(i.type?.toLowerCase())).length > 0 && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Connected Accounts</h4>
            {integrations
              .filter(i => !['gohighlevel', 'highlevel'].includes(i.type?.toLowerCase()))
              .map((integration) => {
              const integType = personalIntegrationTypes.find(t => t.type === integration.type);
              const isConnected = integration.status === 'active' || integration.status === 'connected';
              return (
                <div
                  key={integration.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors bg-white"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                        <span className="text-white text-lg">
                          {integType?.icon || '🔗'}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                        <p className="text-sm text-gray-600 capitalize">{integration.type.replace('_', ' ')}</p>
                        {integration.connected_email && (
                          <p className="text-xs text-gray-500">{integration.connected_email}</p>
                        )}
                        {integration.last_sync && (
                          <p className="text-xs text-gray-500">
                            Last sync: {new Date(integration.last_sync).toLocaleString()}
                          </p>
                        )}
                        <div className="flex items-center space-x-1 mt-1">
                          {isConnected ? (
                            <>
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              <span className="text-xs text-green-600">Connected</span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-3 h-3 text-red-500" />
                              <span className="text-xs text-red-600">Connection Error</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingIntegration(integration)}
                        className="p-2 text-gray-400 hover:text-purple-600 transition-colors"
                        title="Edit Integration"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteIntegration(integration.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Remove Integration"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* No Integrations State */}
        {integrations.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExternalLink className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Personal Integrations Yet</h3>
            <p className="text-gray-600 mb-4">
              Connect Google Calendar to import meetings into Athena
            </p>
            <p className="text-sm text-gray-500">
              Click on one of the available integrations above to get started
            </p>
          </div>
        )}
      </div>

      {/* Integration Setup Wizard */}
      {showWizard && selectedIntegration && (
        <IntegrationSetupWizard
          isOpen={showWizard}
          onClose={() => {
            setShowWizard(false);
            setSelectedIntegration(null);
          }}
          user={user}
          selectedIntegration={selectedIntegration}
        />
      )}
    </>
  );
};

export default PersonalIntegrations;



